import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Slideshow as SlideshowIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  CompareArrows as CompareIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
  AttachMoney as MoneyIcon,
  Support as SupportIcon,
  RocketLaunch as RocketIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

interface SalesPitchDeckProps {
  salesDeck: any;
  onClose: () => void;
}

const SalesPitchDeck: React.FC<SalesPitchDeckProps> = ({ salesDeck, onClose }) => {
  const [activeTab, setActiveTab] = useState('executive');

  const renderExecutiveSummary = () => (
    <Box sx={{ 
      bgcolor: '#3C4545', // Slate 150
      borderRadius: 3,
      color: 'white',
      p: 8,
      mb: 3,
      position: 'relative',
      overflow: 'hidden',
      textAlign: 'center'
    }}>
      {/* Background decoration */}
      <Box sx={{ 
        position: 'absolute',
        top: -50,
        right: -50,
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h1" sx={{ 
          fontWeight: 900,
          mb: 3,
          fontSize: { xs: '3rem', md: '4.5rem' },
          textAlign: 'center',
          letterSpacing: '-0.02em'
        }}>
          Strategic Cloud Transformation
        </Typography>
        
        <Typography variant="h3" sx={{ 
          fontWeight: 300,
          mb: 6,
          fontSize: { xs: '1.5rem', md: '2.2rem' },
          textAlign: 'center',
          opacity: 0.9,
          letterSpacing: '0.01em'
        }}>
          Why Industry Leaders Choose Oracle Cloud Infrastructure
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 3,
          mt: 6
        }}>
          {(salesDeck.executive_summary?.key_points?.slice(0, 3) || [
            'Up to 50% cost reduction vs AWS',
            'Enterprise-grade performance',
            'Seamless migration path'
          ]).map((point: string, index: number) => (
            <Box key={index} sx={{ 
              textAlign: 'center',
              minWidth: 200,
              maxWidth: 300
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                border: '3px solid rgba(255,255,255,0.3)'
              }}>
                <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: '1.3rem',
                lineHeight: 1.3
              }}>
                {point}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const renderCurrentState = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#C74634', // Oracle Red
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Current AWS Spend
        </Typography>
        <Typography variant="h1" sx={{ 
          fontWeight: 900,
          mb: 2,
          fontSize: { xs: '4rem', md: '6rem' },
          letterSpacing: '-0.02em'
        }}>
          ${salesDeck.current_state_analysis?.aws_spend_breakdown?.annual_projection?.toLocaleString() || '4,800,000'}
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Annual Infrastructure Cost
        </Typography>
      </Box>

      <CardContent sx={{ p: 5 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          maxWidth: 900,
          mx: 'auto'
        }}>
          {(salesDeck.current_state_analysis?.pain_points_identified?.slice(0, 3) || [
            'Rising costs without visibility',
            'Complex pricing model',
            'Performance bottlenecks'
          ]).map((point: string, index: number) => (
            <Box key={index} sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <WarningIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: 'warning.dark',
                fontSize: { xs: '1.2rem', md: '1.3rem' },
                lineHeight: 1.3
              }}>
                {point}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderOCIEducation = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#4F7D7B', // Teal
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Oracle Cloud Infrastructure
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Enterprise-Grade Cloud Platform
        </Typography>
      </Box>

      <CardContent sx={{ p: 5 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          maxWidth: 1000,
          mx: 'auto'
        }}>
          {(salesDeck.oci_education?.key_differentiators?.slice(0, 3) || [
            { title: 'Superior Performance', description: '2x faster compute at 50% lower cost' },
            { title: 'Enterprise Security', description: 'Built for mission-critical workloads' },
            { title: 'Seamless Migration', description: 'Lift-and-shift with minimal changes' }
          ]).map((diff: any, index: number) => (
            <Box key={index} sx={{ 
              textAlign: 'center',
              p: 3
            }}>
              <Box sx={{ 
                width: 100, 
                height: 100, 
                borderRadius: '50%', 
                bgcolor: '#C74634', // Oracle Red
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'white',
                mx: 'auto',
                mb: 3
              }}>
                <StarIcon sx={{ fontSize: 50 }} />
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: 'primary.main',
                mb: 2,
                fontSize: { xs: '1.5rem', md: '1.8rem' }
              }}>
                {diff.title}
              </Typography>
              
              <Typography variant="h6" sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.4,
                color: 'grey.600',
                fontWeight: 400
              }}>
                {diff.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderValueProposition = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#4C825C', // Pine (Tech)
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Migration value
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Quantified business impact
        </Typography>
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ 
            fontWeight: 800,
            mb: 4,
            fontSize: { xs: '3rem', md: '4rem' },
            color: '#4C825C' // Pine (Tech)
          }}>
            ${salesDeck.migration_value_proposition?.financial_benefits?.immediate_savings?.annual?.toLocaleString() || '2,400,000'}
          </Typography>
          
          <Typography variant="h4" sx={{ 
            fontWeight: 300,
            color: '#3C4545', // Slate 150
            mb: 2,
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}>
            Annual cost savings
          </Typography>
          
          <Typography variant="h6" sx={{ 
            fontWeight: 400,
            color: '#697778', // Slate 100
            fontSize: '1.2rem'
          }}>
            Immediate reduction from AWS spend
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          maxWidth: 900,
          mx: 'auto'
        }}>
          {(salesDeck.migration_value_proposition?.operational_benefits?.slice(0, 3) || [
            'Enhanced performance',
            'Simplified management',
            'Improved reliability'
          ]).map((benefit: string, index: number) => (
            <Box key={index} sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#4C825C', // Pine (Tech)
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: '#3C4545', // Slate 150
                fontSize: { xs: '1.3rem', md: '1.5rem' }
              }}>
                {benefit}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderMigrationPlan = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#3C4545', // Slate 150
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Migration roadmap
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Your journey to Oracle Cloud
        </Typography>
      </Box>

      <CardContent sx={{ p: 5, bgcolor: '#F1EFED' }}> {/* Neutral 30 */}
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {[
            { 
              title: 'Assessment & planning', 
              duration: '2-4 weeks',
              key_activities: ['Infrastructure audit', 'Migration strategy', 'Timeline creation']
            },
            { 
              title: 'Pilot migration', 
              duration: '4-6 weeks',
              key_activities: ['Non-critical workloads', 'Process validation', 'Team training']
            },
            { 
              title: 'Full migration', 
              duration: '8-12 weeks',
              key_activities: ['Production workloads', 'Optimization', 'Go-live support']
            }
          ].map((phase: any, index: number) => (
            <Card key={index} sx={{ 
              mb: 4,
              p: 4,
              border: '2px solid #C2D4D4', // Slate 50
              borderRadius: 3,
              bgcolor: 'white',
              position: 'relative'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                left: 30,
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: '#C74634', // Oracle Red
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 700
              }}>
                {index + 1}
              </Box>

              <Box sx={{ ml: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700,
                    color: '#3C4545', // Slate 150
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}>
                    {phase.title}
                  </Typography>
                  <Chip 
                    label={phase.duration}
                    sx={{
                      bgcolor: '#4F7D7B', // Teal accent
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 2
                }}>
                  {phase.key_activities.map((activity: string, actIndex: number) => (
                    <Box key={actIndex} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      bgcolor: '#F1EFED', // Neutral 30
                      borderRadius: 2
                    }}>
                      <CheckIcon sx={{ color: '#4F7D7B', fontSize: 18 }} />
                      <Typography variant="body1" sx={{ 
                        fontWeight: 500,
                        color: '#3C4545', // Slate 150
                        fontSize: '1rem'
                      }}>
                        {activity}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderCompetitiveAnalysis = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#C74634', // Oracle Red
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Why Oracle Cloud wins
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Head-to-head competitive advantage
        </Typography>
      </Box>

      <CardContent sx={{ p: 5, bgcolor: '#F1EFED' }}> {/* Neutral 30 */}
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4
          }}>
            {(salesDeck.competitive_differentiation?.head_to_head_comparison?.slice(0, 3) || [
              {
                category: 'Performance',
                aws_position: 'Variable performance',
                oci_advantage: '2x faster compute',
                customer_impact: 'Faster applications'
              },
              {
                category: 'Pricing',
                aws_position: 'Complex pricing model',
                oci_advantage: 'Transparent, predictable',
                customer_impact: 'Up to 50% cost savings'
              },
              {
                category: 'Security',
                aws_position: 'Shared responsibility',
                oci_advantage: 'Built-in enterprise security',
                customer_impact: 'Enhanced data protection'
              }
            ]).map((comp: any, index: number) => (
              <Card key={index} sx={{ 
                p: 4,
                border: '2px solid #C2D4D4', // Slate 50
                borderRadius: 3,
                bgcolor: 'white',
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700,
                  color: '#3C4545', // Slate 150
                  mb: 3,
                  fontSize: { xs: '1.5rem', md: '1.8rem' }
                }}>
                  {comp.category}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ 
                    color: '#697778', // Slate 100
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em'
                  }}>
                    AWS Challenge
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: '#3C4545', // Slate 150
                    fontWeight: 400,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}>
                    {comp.aws_position}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ 
                    color: '#4F7D7B', // Teal
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em'
                  }}>
                    OCI Advantage
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: '#4F7D7B', // Teal
                    fontWeight: 600,
                    mb: 3,
                    fontSize: '1.1rem'
                  }}>
                    {comp.oci_advantage}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  p: 3,
                  bgcolor: '#F1B13F', // Brand Yellow
                  borderRadius: 2
                }}>
                  <Typography variant="h6" sx={{ 
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    {comp.customer_impact}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderROIAnalysis = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#4F7D7B', // Teal
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Return on investment
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Business case summary
        </Typography>
      </Box>

      <CardContent sx={{ p: 5, bgcolor: '#F1EFED' }}> {/* Neutral 30 */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ 
            fontWeight: 900,
            mb: 2,
            fontSize: { xs: '4rem', md: '6rem' },
            color: '#4F7D7B', // Teal
            letterSpacing: '-0.02em'
          }}>
            {salesDeck.roi_and_business_case?.investment_summary?.three_year_roi || '320%'}
          </Typography>
          
          <Typography variant="h4" sx={{ 
            fontWeight: 300,
            color: '#3C4545', // Slate 150
            mb: 2,
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}>
            3-year ROI
          </Typography>
          
          <Typography variant="h6" sx={{ 
            fontWeight: 400,
            color: '#697778', // Slate 100
            fontSize: '1.1rem'
          }}>
            Total return on Oracle Cloud investment
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          maxWidth: 900,
          mx: 'auto'
        }}>
          <Card sx={{ 
            p: 4,
            textAlign: 'center',
            border: '2px solid #C2D4D4', // Slate 50
            borderRadius: 3,
            bgcolor: 'white'
          }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 800,
              color: '#4F7D7B', // Teal
              mb: 1,
              fontSize: { xs: '2.5rem', md: '3rem' }
            }}>
              ${salesDeck.roi_and_business_case?.investment_summary?.annual_savings?.toLocaleString() || '2,400,000'}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#3C4545', // Slate 150
              fontWeight: 600,
              fontSize: '1.1rem'
            }}>
              Annual savings
            </Typography>
          </Card>
          
          <Card sx={{ 
            p: 4,
            textAlign: 'center',
            border: '2px solid #C2D4D4', // Slate 50
            borderRadius: 3,
            bgcolor: 'white'
          }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 800,
              color: '#4F7D7B', // Teal
              mb: 1,
              fontSize: { xs: '2.5rem', md: '3rem' }
            }}>
              {salesDeck.roi_and_business_case?.investment_summary?.payback_period || '8 months'}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#3C4545', // Slate 150
              fontWeight: 600,
              fontSize: '1.1rem'
            }}>
              Payback period
            </Typography>
          </Card>
          
          <Card sx={{ 
            p: 4,
            textAlign: 'center',
            border: '2px solid #C2D4D4', // Slate 50
            borderRadius: 3,
            bgcolor: 'white'
          }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 800,
              color: '#C74634', // Oracle Red
              mb: 1,
              fontSize: { xs: '2.5rem', md: '3rem' }
            }}>
              $0
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#3C4545', // Slate 150
              fontWeight: 600,
              fontSize: '1.1rem'
            }}>
              Migration cost
            </Typography>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );

  const renderNextSteps = () => (
    <Card elevation={3} sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 6, 
        bgcolor: '#C74634', // Oracle Red
        color: 'white',
        textAlign: 'center'
      }}>
        <Typography variant="h2" sx={{ 
          fontWeight: 800,
          mb: 2,
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          letterSpacing: '-0.01em'
        }}>
          Next steps
        </Typography>
        <Typography variant="h5" sx={{ 
          opacity: 0.9,
          fontWeight: 300,
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}>
          Start your Oracle Cloud journey today
        </Typography>
      </Box>

      <CardContent sx={{ p: 5, bgcolor: '#F1EFED' }}> {/* Neutral 30 */}
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 4,
            mb: 6
          }}>
            {(salesDeck.next_steps_and_timeline?.immediate_actions?.slice(0, 3) || [
              { action: 'Schedule assessment', timeline: 'This week', outcome: 'Understanding current infrastructure' },
              { action: 'Review migration plan', timeline: 'Next week', outcome: 'Detailed roadmap and timeline' },
              { action: 'Begin pilot migration', timeline: '2-4 weeks', outcome: 'Proof of concept validation' }
            ]).map((action: any, index: number) => (
              <Card key={index} sx={{ 
                p: 4,
                border: '2px solid #C2D4D4', // Slate 50
                borderRadius: 3,
                bgcolor: 'white',
                textAlign: 'center',
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  bgcolor: '#4F7D7B', // Teal
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 700
                }}>
                  {index + 1}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                    color: '#3C4545', // Slate 150
                    mb: 2,
                    fontSize: { xs: '1.3rem', md: '1.5rem' }
                  }}>
                    {action.action}
                  </Typography>
                  
                  <Chip 
                    label={action.timeline}
                    sx={{
                      bgcolor: '#F1B13F', // Brand Yellow
                      color: 'white',
                      fontWeight: 600,
                      mb: 2
                    }}
                  />
                  
                  <Typography variant="body1" sx={{ 
                    color: '#697778', // Slate 100
                    fontWeight: 400,
                    fontSize: '1rem'
                  }}>
                    {action.outcome}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>

          <Card sx={{ 
            p: 4,
            bgcolor: '#C74634', // Oracle Red
            color: 'white',
            textAlign: 'center',
            borderRadius: 3
          }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.5rem', md: '1.8rem' }
            }}>
              Ready to get started?
            </Typography>
            <Typography variant="h6" sx={{ 
              fontWeight: 300,
              mb: 3,
              opacity: 0.9,
              fontSize: '1.1rem'
            }}>
              Contact your Oracle Cloud specialist today
            </Typography>
            <Button 
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#C74634',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: '#F1EFED'
                }
              }}
            >
              Schedule consultation
            </Button>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', bgcolor: '#F1EFED', minHeight: '100vh' }}> {/* Neutral 30 */}
      <Card sx={{ mb: 3, p: 4, bgcolor: 'white', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: '#3C4545', // Slate 150
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}>
            <SlideshowIcon sx={{ mr: 2, fontSize: 36, color: '#C74634' }} /> {/* Oracle Red */}
            {salesDeck.deck_title || 'Oracle Cloud Infrastructure Sales Pitch'}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={onClose}
            sx={{
              borderColor: '#C74634', // Oracle Red
              color: '#C74634',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#C74634',
                bgcolor: 'rgba(199, 70, 52, 0.1)'
              }
            }}
          >
            Close Deck
          </Button>
        </Box>
      </Card>

      <Card sx={{ mb: 3, bgcolor: 'white', borderRadius: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            '& .MuiTabs-indicator': { 
              height: 4,
              backgroundColor: '#C74634' // Oracle Red
            },
            '& .MuiTab-root': { 
              minHeight: 64,
              fontWeight: 600,
              fontSize: '1rem',
              color: '#697778', // Slate 100
              '&.Mui-selected': {
                color: '#C74634' // Oracle Red
              }
            },
            p: 1
          }}
        >
          <Tab label="Executive summary" value="executive" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Current state" value="current" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="OCI overview" value="education" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Value proposition" value="value" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Migration plan" value="migration" icon={<TimelineIcon />} iconPosition="start" />
          <Tab label="Competitive analysis" value="competitive" icon={<CompareIcon />} iconPosition="start" />
          <Tab label="ROI analysis" value="roi" icon={<MoneyIcon />} iconPosition="start" />
          <Tab label="Next steps" value="next" icon={<RocketIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      <Box sx={{ mb: 4 }}>
        {activeTab === 'executive' && renderExecutiveSummary()}
        {activeTab === 'current' && renderCurrentState()}
        {activeTab === 'education' && renderOCIEducation()}
        {activeTab === 'value' && renderValueProposition()}
        {activeTab === 'migration' && renderMigrationPlan()}
        {activeTab === 'competitive' && renderCompetitiveAnalysis()}
        {activeTab === 'roi' && renderROIAnalysis()}
        {activeTab === 'next' && renderNextSteps()}
      </Box>
    </Box>
  );
};

export default SalesPitchDeck;